/**
 * ChatTextArea - Rich text input with file attachments
 *
 * Features:
 * - Multi-line text input with auto-resize
 * - RTL/LTR text direction detection (Arabic)
 * - File attachments via click, drag-drop, or paste
 * - File upload progress and error handling
 * - Submit on Enter, new line on Shift+Enter
 * - Loading state with stop button
 * - Error and warning notifications
 *
 * @example
 * ```tsx
 * <ChatTextArea
 *   onSubmit={(text, attachmentIds) => console.log(text, attachmentIds)}
 *   onFileUploaded={(id, ctx) => analytics.track('file_uploaded', ctx)}
 *   maxFiles={10}
 *   maxFileSize={102400}
 * />
 * ```
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  CirclePlus,
  Clock,
  FilePlus,
  Send,
  TriangleAlert,
  X,
  StopCircle,
  AppWindow,
  Sparkles,
} from 'lucide-react';
import { Button } from '../atoms/button';
import FilePreview from './file-preview';
import type {
  ChatTextAreaProps,
  AttachedFile,
  UseFileAttachmentsHook,
  LLMModel,
} from '../../types/ChatTextArea.types';
// Analytics: file.uploaded tracked via Redux middleware (old import removed)
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../atoms/tooltip';

// Re-export types for convenience
export type {
  ChatTextAreaProps,
  AttachedFile,
  UseFileAttachmentsHook,
  LLMModel,
} from '../../types/ChatTextArea.types';

// Utility function to detect Arabic text
const isArabicText = (text: string): boolean => {
  if (!text.trim()) return false;

  // Arabic Unicode ranges
  // Arabic: U+0600-U+06FF
  // Arabic Supplement: U+0750-U+077F
  // Arabic Extended-A: U+08A0-U+08FF
  // Arabic Presentation Forms-A: U+FB50-U+FDFF
  // Arabic Presentation Forms-B: U+FE70-U+FEFF
  const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  // Check the first character for direction
  return arabicRegex.test(text.charAt(0));
};

// Props with platform-specific hook injected
export interface ChatTextAreaInternalProps extends ChatTextAreaProps {
  /**
   * Platform-specific file attachments hook
   * Must be provided by @ideoz/web or @ideoz/plugin
   */
  useFileAttachmentsHook: (options: {
    maxFiles: number;
    maxFileSize: number;
    maxTotalSize: number;
    allowedFileTypes: string[];
    onUploadComplete?: (attachmentId: string) => void;
    onUploadError?: (error: string) => void;
  }) => UseFileAttachmentsHook;
}

const ChatTextAreaInternal: React.FC<ChatTextAreaInternalProps> = ({
  variant = 'default',
  onSubmit,
  onCancel,
  placeholder = 'Type your message...',
  disabled = false,
  className = '',
  rows = 2,
  maxFiles = 10,
  maxFileSize = 102400, // 100KB default
  maxTotalSize = 102400, // 100KB total default
  allowedFileTypes = [
    'text/plain',
    'text/markdown',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    // PowerPoint files
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.ms-powerpoint', // .ppt
    // Excel files
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls
    // Images
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  isChatLoading = false,
  isWeb = true,
  onFileUploaded,
  onLargeFilesDetected,
  onUnsupportedFilesDetected,
  analyticsContext,
  initialLlmFamily = 'Gemini',
  onLlmFamilyChange,
  useFileAttachmentsHook,
  isPrototypeOpen,
  isPrototypeCreating,
  currentMode,
  onGeneratePrototype,
  onBackToUncover,
  hasPrototypeCredits = true,
  hasProject = false,
  initiatedFromUncover = false,
}) => {
  const [text, setText] = useState('');
  const [textDirection, setTextDirection] = useState<'ltr' | 'rtl'>('ltr');
  const [isDragging, setIsDragging] = useState(false);
  // State for error details
  const [errorDetails, setErrorDetails] = useState<{
    summary: string;
    files: string[];
  } | null>(null);
  const [showErrorBar, setShowErrorBar] = useState(false);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [showWarningBar, setShowWarningBar] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  // Normalize initialLlmFamily: default to 'Gemini' and migrate 'DeepSeek' to 'Gemini'
  const normalizedInitialLlmFamily: LLMModel =
    !initialLlmFamily || initialLlmFamily === 'DeepSeek' ? 'Gemini' : initialLlmFamily;

  const [selectedModel, setSelectedModel] = useState<LLMModel>(normalizedInitialLlmFamily);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use file attachments hook (injected from platform)
  const { attachments, isUploading, addFiles, removeFile, clearAttachments, getAttachmentIds } =
    useFileAttachmentsHook({
      maxFiles,
      maxFileSize,
      maxTotalSize,
      allowedFileTypes,
      onUploadComplete: (attachmentId) => {
        console.log('File uploaded successfully:', attachmentId);
        // Determine location context
        let chatLocation = 'landing page chat';
        if (analyticsContext?.projectId) {
          chatLocation = 'project chat';
        } else if (analyticsContext?.isConversation) {
          chatLocation = 'conversation chat';
        }
        // Analytics: file.uploaded tracked via Redux middleware

        // Fire callback for analytics tracking
        onFileUploaded?.(attachmentId, {
          location: chatLocation,
          projectId: analyticsContext?.projectId,
        });
      },
      onUploadError: (error) => {
        // Parse error to extract file details if available
        let summary = error;
        let files: string[] = [];

        // Pattern 1: Files exceeding size limit
        // "10 files exceeding 100KB and cannot be uploaded. Please compress them, then try again: file1.pdf, file2.pdf, ..."
        const sizeMatch = error.match(/(\d+)\s+files?\s+exceeding\s+(\d+)KB.*?try again:\s*(.*)/);
        if (sizeMatch) {
          const [, fileCount, sizeLimit, fileList] = sizeMatch;
          summary = `${fileCount} files exceeding ${sizeLimit}KB and cannot be uploaded. Please compress them, then try again`;
          files = fileList
            .split(',')
            .map((f) => f.trim())
            .filter((f) => f);
        } else {
          // Pattern 2: Files with unsupported formats
          // "3 files with unsupported formats cannot be uploaded. Allowed types: ...: file1.exe, file2.bat, ..."
          const formatMatch = error.match(
            /(\d+)\s+files?\s+with\s+unsupported\s+formats?.*?Images:\s*(.*)/
          );
          if (formatMatch) {
            const [, , fileList] = formatMatch;
            const allowedPart = error.substring(0, error.lastIndexOf(':'));
            summary = allowedPart;
            files = fileList
              .split(',')
              .map((f) => f.trim())
              .filter((f) => f);
          } else {
            // Pattern 3: Maximum files limit
            // "Maximum 10 files allowed. 5 files cannot be added: file11.pdf, file12.pdf, ..."
            const limitMatch = error.match(
              /Maximum\s+(\d+)\s+files\s+allowed\.\s+(\d+)\s+files?\s+cannot\s+be\s+added:\s*(.*)/
            );
            if (limitMatch) {
              const [, maxFiles, excessCount, fileList] = limitMatch;
              summary = `Maximum ${maxFiles} files allowed. ${excessCount} files cannot be added`;
              files = fileList
                .split(',')
                .map((f) => f.trim())
                .filter((f) => f);
            } else {
              // Pattern 4: Single file upload errors
              // "Failed to upload "document.pdf": Server error"
              const uploadMatch = error.match(/Failed to upload "(.*?)":\s*(.*)/);
              if (uploadMatch) {
                const [, fileName, errorMsg] = uploadMatch;
                summary = `Failed to upload file: ${errorMsg}`;
                files = [fileName];
              } else {
                // Fallback: try to extract any filenames with extensions from the error
                const filePattern =
                  /[^\s,]+\.(pdf|doc|docx|txt|md|ppt|pptx|xls|xlsx|jpg|jpeg|png|gif|webp)/gi;
                const foundFiles = error.match(filePattern) || [];
                if (foundFiles.length > 0) {
                  files = foundFiles;
                  // Remove file names from summary to avoid duplication
                  summary = error
                    .replace(
                      new RegExp(
                        foundFiles.map((f) => f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
                        'gi'
                      ),
                      ''
                    )
                    .replace(/:\s*$/, '')
                    .trim();
                }
              }
            }
          }
        }

        setErrorDetails({
          summary,
          files,
        });
      },
    });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  // Sync selectedModel with initialLlmFamily prop (normalize DeepSeek to Gemini)
  useEffect(() => {
    const normalized =
      !initialLlmFamily || initialLlmFamily === 'DeepSeek' ? 'Gemini' : initialLlmFamily;
    if (normalized !== selectedModel) {
      setSelectedModel(normalized);
    }
  }, [initialLlmFamily, selectedModel]);

  // Handle text change with direction detection
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);

    // Detect text direction based on first character
    if (newText.trim()) {
      const isArabic = isArabicText(newText);
      setTextDirection(isArabic ? 'rtl' : 'ltr');
    } else {
      // Reset to LTR when text is empty
      setTextDirection('ltr');
    }
  };

  // Convert platform attachments to AttachedFile for FilePreview component
  const convertToAttachedFiles = (
    platformAttachments: UseFileAttachmentsHook['attachments']
  ): AttachedFile[] => {
    return platformAttachments.map((att) => ({
      id: att.id,
      file: att.file,
      preview: att.preview,
      isImage: att.isImage,
      isUploading: att.isUploading,
      uploadProgress: att.uploadProgress,
      error: att.error,
    }));
  };

  // Handle file uploads and show appropriate messages
  const handleAddFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      // Log any files larger than 10MB (does not block upload, just informational)
      const TEN_MB = 10 * 1024 * 1024; // 10MB in bytes
      const largeFiles = fileArray.filter((f) => f.size > TEN_MB);
      if (largeFiles.length > 0) {
        // Fire callback for analytics tracking
        onLargeFilesDetected?.(largeFiles.length, {
          location: analyticsContext?.isConversation ? 'conversation chat' : 'landing page chat',
        });
      }

      // Check for unsupported files and show warning if needed
      const unsupportedFiles = fileArray.filter((file) => !allowedFileTypes.includes(file.type));

      if (unsupportedFiles.length > 0) {
        const unsupportedTypes = [...new Set(unsupportedFiles.map((f) => f.type))];
        setWarningMessage(`Unsupported file types: ${unsupportedTypes.join(', ')}`);

        // Fire callback for analytics tracking
        onUnsupportedFilesDetected?.(unsupportedFiles.length, unsupportedTypes, {
          location: analyticsContext?.isConversation ? 'conversation chat' : 'landing page chat',
        });
      }

      // Filter to only supported files
      const supportedFiles = fileArray.filter((file) => allowedFileTypes.includes(file.type));

      if (supportedFiles.length > 0) {
        try {
          await addFiles(supportedFiles);
        } catch (error) {
          console.error('Error adding files:', error);
        }
      }
    },
    [addFiles, allowedFileTypes, onLargeFilesDetected, onUnsupportedFilesDetected, analyticsContext]
  );

  // Handle file removal
  const handleRemoveFile = useCallback(
    async (fileId: string) => {
      // Guard against duplicate clicks while removal is in flight
      if (deletingIds.has(fileId)) return;
      setDeletingIds((prev) => new Set(prev).add(fileId));
      try {
        await removeFile(fileId);
        // Clear error and warning messages when files are removed
        setErrorDetails(null);
        setWarningMessage('');
      } catch (error) {
        // Swallow duplicate/not-found errors silently to avoid noisy UI
        console.error('Error removing file:', error);
      } finally {
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
      }
    },
    [removeFile, deletingIds]
  );

  // Auto-hide error message after 5 seconds and handle animation
  useEffect(() => {
    if (errorDetails) {
      setShowErrorBar(true);
      const timer = setTimeout(() => {
        setShowErrorBar(false);
        setTimeout(() => setErrorDetails(null), 300); // Wait for slide-out animation
      }, 6000);
      return () => clearTimeout(timer);
    } else {
      setShowErrorBar(false);
      return undefined;
    }
  }, [errorDetails]);

  // Auto-hide warning message after 5 seconds and handle animation
  useEffect(() => {
    if (warningMessage) {
      setShowWarningBar(true);
      const timer = setTimeout(() => {
        setShowWarningBar(false);
        setTimeout(() => setWarningMessage(''), 300); // Wait for slide-out animation
      }, 6000);
      return () => clearTimeout(timer);
    } else {
      setShowWarningBar(false);
      return undefined;
    }
  }, [warningMessage]);

  // Handle paste events
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        await handleAddFiles(files);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleAddFiles]);

  // Handle drag and drop
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        await handleAddFiles(files);
      }
    },
    [handleAddFiles]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter: Create new line (default behavior)
        return;
      } else {
        // Enter: Submit
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleSubmit = async () => {
    if ((!text.trim() && attachments.length === 0) || !onSubmit || isUploading) return;
    setIsSubmitting(true);
    setTextDirection('ltr'); // Reset to LTR after submission
    try {
      // Get attachment IDs and submit
      const attachmentIds = getAttachmentIds();
      onSubmit(text, attachmentIds, selectedModel); // Pass selected model as llm_family

      // Clear form
      setText('');
      clearAttachments();
    } catch (error) {
      console.error('Error submitting message:', error);
      setErrorDetails({
        summary: 'Failed to submit message. Please try again.',
        files: [],
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleFilesClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await handleAddFiles(files);
      // Clear input so same file can be selected again
      e.target.value = '';
    }
  };

  const containerClasses = variant === 'dimmed' ? 'opacity-60 bg-gray-50' : 'bg-white';

  const dragOverlayClasses = isDragging
    ? 'absolute inset-0 bg-primary-50 border-2 border-dashed border-primary-400 rounded-2xl flex items-center justify-center z-10'
    : 'hidden h-0';

  return (
    <div className="relative w-full">
      {/* Error message bar with slide animation - absolutely positioned */}
      <div
        className={`absolute top-0 left-0 w-full z-20 overflow-hidden transition-all duration-300 ease-in-out ${
          showErrorBar && errorDetails
            ? 'max-h-32 opacity-100 -translate-y-[96%]'
            : 'max-h-0 opacity-0 -translate-y-full'
        }`}
      >
        <div className="p-3 bg-error-50 text-start flex flex-col gap-2 text-error-600 transform transition-all duration-300 ease-in-out">
          <div className="flex items-center gap-2">
            <TriangleAlert
              size={16}
              strokeWidth={2.5}
              color="#D92D20"
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <div className="text-xs font-normal">{errorDetails?.summary}</div>
            </div>
            <button
              onClick={() => {
                setShowErrorBar(false);
                setTimeout(() => setErrorDetails(null), 300);
              }}
              className="p-1 hover:bg-red-100 rounded transition-colors flex-shrink-0"
              type="button"
            >
              <X size={20} strokeWidth={2.5} color="#B42318" />
            </button>
          </div>
          <div className="px-6 py-1">
            {errorDetails?.files && errorDetails.files.length > 0 && (
              <div className="text-xs leading-5 max-h-16 overflow-hidden">
                {(() => {
                  const files = errorDetails.files;
                  const maxChars = 335;
                  let displayFiles: string[] = [];
                  let currentLength = 0;

                  // Dynamically determine how many files to display based on character limit
                  for (let i = 0; i < files.length; i++) {
                    const fileWithComma = i === 0 ? files[i] : ', ' + files[i];
                    if (currentLength + fileWithComma.length <= maxChars) {
                      displayFiles.push(files[i]);
                      currentLength += fileWithComma.length;
                    } else {
                      break;
                    }
                  }

                  const remainingCount = files.length - displayFiles.length;

                  return (
                    <div className="break-words">
                      {displayFiles.map((file, index) => (
                        <span key={index}>
                          {file}
                          {index < displayFiles.length - 1 && ', '}
                        </span>
                      ))}
                      {remainingCount > 0 && (
                        <span className="">, + {remainingCount} other files</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Warning message bar with slide animation - absolutely positioned */}
      <div
        className={`absolute top-0 left-0 w-full z-20 overflow-hidden transition-all duration-300 ease-in-out ${
          showWarningBar && warningMessage
            ? 'max-h-20 opacity-100 -translate-y-[96%]'
            : 'max-h-0 opacity-0 -translate-y-full'
        }`}
      >
        <div className="p-2 bg-yellow-50 border-l-4 border-l-yellow-400 border-t border-r border-yellow-200 rounded-t-lg flex gap-2 text-yellow-600 transform transition-all duration-300 ease-in-out">
          <Clock size={16} strokeWidth={2.5} color="#DC6803" />
          <span className="text-sm font-medium flex-1 text-start">{warningMessage}</span>
          <button
            onClick={() => {
              setShowWarningBar(false);
              setTimeout(() => setWarningMessage(''), 300);
            }}
            className="p-1 hover:bg-yellow-100 rounded transition-colors"
            type="button"
          >
            <X size={14} strokeWidth={2.5} color="#667085" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className={`relative flex flex-col w-full glass rounded-2xl p-6 glass-glow justify-center items-end gap-2 focus-within:shadow-lg transition-all duration-500 ease-in-out border border-neutral-200 ${
          isWeb ? 'min-h-[89px]' : 'min-h-[65.896px]'
        } ${containerClasses} ${className} ${isPrototypeOpen ? '!w-full' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        <div className={dragOverlayClasses}>
          <div className="text-center">
            <FilePlus size={26} strokeWidth={2.5} />
            <p className="text-sm font-medium text-primary-700">Drop files here to attach</p>
          </div>
        </div>

        {/* File attachments preview */}
        <FilePreview
          files={convertToAttachedFiles(attachments)}
          onDelete={handleRemoveFile}
          hasDelete={true}
        />

        {/* Textarea */}
        <div className="w-full">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            dir={textDirection}
            className="w-full overflow-y-auto scrollbar-chat max-h-[100px] min-h-6 resize-none border-none outline-none bg-transparent text-base font-normal leading-5 placeholder-gray-300 placeholder:text-base placeholder:font-normal"
            rows={rows}
            data-testid="chat-input"
            autoFocus={true}
          />
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          accept={allowedFileTypes.join(',')}
          data-testid="chat-file-input"
        />

        {/* Bottom bar */}
        <div className="flex justify-between items-end self-stretch pe-1 relative top-3 -mx-3">
          <div className="flex gap-2 items-center">
            {/* Left div - Model selector */}
            {/* <div className="relative">
              <button
                type="button"
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                className="flex py-2 px-3 items-center gap-1 rounded-2xl bg-primary-100 hover:bg-primary-200 transition-colors"
                disabled={disabled}
                data-testid="model-selector-button"
              >
                <span className="text-gray-500 text-xs font-normal leading-[150%] tracking-[-0.088px]">
                  {selectedModel}
                </span>
                <ChevronDown size={12} className="text-gray-500" />
              </button>

             
              {isModelDropdownOpen && (
                <>
              
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsModelDropdownOpen(false)}
                  />
             
                  <div className="absolute bottom-full left-0 mb-1 w-32 bg-white rounded-2xl shadow-lg border border-gray-100 py-1 z-20">
                    {(['DeepSeek', 'Grok', 'Gemini'] as LLMModel[]).map((model) => (
                      <button
                        key={model}
                        type="button"
                        onClick={() => {
                          setSelectedModel(model);
                          setIsModelDropdownOpen(false);
                          onLlmFamilyChange?.(model);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-100 transition-colors ${
                          selectedModel === model
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-gray-700'
                        }`}
                        data-testid={`model-option-${model}`}
                      >
                        {model}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div> */}
          </div>
          {/* Right div - Action buttons */}
          <div className="flex items-center gap-1">
            {/* Generate Prototype / Back to Uncover button - only shown in uncover mode when project exists */}
            {(currentMode === 'uncover' || initiatedFromUncover) &&
              hasProject &&
              hasPrototypeCredits && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {currentMode === 'prototyper' ? (
                        <Button
                          variant="tertiary-gray"
                          size="auto"
                          onClick={onBackToUncover}
                          disabled={isChatLoading || isPrototypeCreating || disabled || !isWeb}
                          className="flex items-center gap-1.5 px-3 py-2 hover:bg-blue-light-50 hover:text-blue-600 text-xs font-medium text-gray-600"
                          data-testid="back-to-uncover-button"
                        >
                          <Sparkles size={16} strokeWidth={2} stroke="currentColor" />
                          <span>Back to Uncover</span>
                        </Button>
                      ) : (
                        <Button
                          variant="tertiary-gray"
                          size="auto"
                          onClick={onGeneratePrototype}
                          disabled={
                            isChatLoading || isPrototypeCreating || disabled || !hasPrototypeCredits || !isWeb
                          }
                          className="flex items-center gap-1.5 px-3 py-2 hover:bg-blue-light-50 hover:text-blue-600 disabled:opacity-50 text-xs font-medium text-gray-600"
                          data-testid="generate-prototype-button"
                        >
                          <AppWindow size={16} strokeWidth={2} stroke="currentColor" />
                          <span>Generate Prototype</span>
                        </Button>
                      )}
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {isPrototypeOpen
                          ? 'Return to Uncover mode chat'
                          : hasPrototypeCredits
                            ? 'Generate a prototype from this conversation'
                            : 'No prototype credits available'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            {/* Add button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="tertiary-gray"
                    size="auto"
                    onClick={handleFilesClick}
                    disabled={disabled}
                    className="p-2 hover:bg-blue-light-50 hover:text-blue-600"
                    data-testid="chat-add-files-button"
                  >
                    <CirclePlus size={20} strokeWidth={2} stroke="currentColor" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-semibold">
                    Supported files <span className="font-normal">(Maximum file size 100 KB)</span>
                  </p>
                  <p>JPG, PNG, PDF, DOC, DOCX, TXT, MD, PPT, PPTX, XLS, XLSX</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Send button */}
            {isChatLoading ? (
              <Button
                variant="tertiary-gray"
                size="auto"
                onClick={handleCancel}
                className="p-2 hover:bg-blue-light-50 hover:text-blue-600 disabled:opacity-50"
                data-testid="chat-stop-button"
                data-track="stop_generation"
                disabled={isPrototypeCreating}
              >
                <StopCircle size={20} strokeWidth={2} stroke="currentColor" />
              </Button>
            ) : (
              <Button
                variant="tertiary-gray"
                size="auto"
                onClick={handleSubmit}
                disabled={
                  disabled ||
                  isSubmitting ||
                  isUploading ||
                  (!text.trim() && attachments.length === 0)
                }
                className="p-2 hover:bg-blue-light-50 hover:text-blue-600 disabled:opacity-50"
                data-testid="chat-submit-button"
                data-track="send_message"
              >
                <Send size={20} strokeWidth={2} stroke="currentColor" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the internal component for platform packages to wrap
export { ChatTextAreaInternal };

// Export as default too
export default ChatTextAreaInternal;
